import { Link } from 'react-router-dom';
import {
  MessagesSquare, FileText, Sparkles, Scale, Briefcase, Calculator, Compass,
  Upload, MessageSquare, Swords, ListChecks, ChevronRight, FileSignature, Mic,
} from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';
import { WHATSAPP_DISPLAY, WHATSAPP_URL, WhatsAppIcon } from './landing/data';

const STEPS = [
  {
    icon: MessagesSquare,
    title: 'Ask a legal question',
    body:
      'The chat button is on every page — or use ＋ New → Ask a legal question. Describe ' +
      "what's going on in plain words: type it or speak it, in English, हिंदी or മലയാളം, and " +
      'attach the notice, FIR or judgment if you have one. Urava asks a couple of quick ' +
      'questions to sharpen it into a precise legal question — or tap ' +
      '"Research it exactly as you wrote it" and skip straight ahead.',
  },
  {
    icon: FileText,
    title: 'Get a full memorandum',
    body:
      'Urava searches Supreme Court and High Court judgments, the BNS / BNSS / BSA and other ' +
      'statutes, and live legal sources — then writes a structured memo: issue, applicable law, ' +
      'cited case law, arguments, counter-arguments, and a conclusion. Every citation link is ' +
      'independently verified and the count shown (e.g. "8/8 citations verified"). Ask in Hindi ' +
      'or Malayalam and the memo comes back in that language (citations stay in English, as ' +
      'courts expect). Download as PDF or an editable Word document.',
  },
  {
    icon: Sparkles,
    title: 'Work with it',
    body:
      "The memo isn't the end — every completed memo opens with tabs to pressure-test the " +
      'research before you rely on it in court.',
  },
  {
    icon: FileSignature,
    title: 'Draft the documents',
    body:
      'Pick from 17 Indian document types — demand notices, bail applications, consumer ' +
      'complaints, plaints, writs, RTI applications, rent agreements and more. Urava interviews ' +
      'you one question at a time and skips anything it already knows — attach the notice or ' +
      'plaint you\'re answering and it drafts against its actual dates and paragraphs. Statutory ' +
      'deadlines are checked automatically. Draft from a memo ("Cited from your memo" uses only ' +
      'its verified authorities) or make a standard draft on its own — either way you get an ' +
      'editable Word document.',
  },
  {
    icon: Briefcase,
    title: 'File it under a case',
    body:
      'Every case gets a case file — hearings and your court diary, research, drafts, documents, ' +
      'tasks and people, together like your physical file. Each morning the Today board shows ' +
      'your cause list: today\'s hearings, tomorrow\'s prep, calls to make and tasks due — with ' +
      'hearing reminders and a calendar feed so nothing slips.',
  },
  {
    icon: WhatsAppIcon,
    title: 'Take it to court — on WhatsApp',
    body:
      `In court with no laptop? Send your question — or a PDF — to ${WHATSAPP_DISPLAY} on ` +
      'WhatsApp and the memo comes back right in the chat.',
  },
];

const STUDIO = [
  { icon: MessageSquare, title: 'Discuss', desc: 'Ask anything about the memo — answers grounded in the research.' },
  { icon: ListChecks, title: 'Test me', desc: 'Check your command of it before a hearing, viva, or briefing.' },
  { icon: Swords, title: 'Opposing counsel', desc: 'Urava argues the other side so you can defend yours.' },
  { icon: FileSignature, title: 'Drafts', desc: 'Turn the memo into filings that cite only its verified authorities.' },
];

const POPULAR = [
  'My landlord won’t return my security deposit — what can I do?',
  'A cheque issued to me has bounced. What are my options under the new law?',
  'What are the grounds for anticipatory bail under the BNSS?',
  'Compare cheating under Section 318 BNS with Section 420 IPC.',
  'Draft cross-examination points for a hostile eyewitness in a murder trial.',
  'How is maintenance for a working wife calculated under Section 144 BNSS?',
];

export function UserGuidePage() {
  return (
    <div className="min-h-screen bg-legal-900 overflow-x-hidden">
      <PublicNav />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 text-accent text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <Scale className="h-4 w-4" />
            <span>How it works</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How Urava works
          </h1>
          <p className="text-lg text-legal-300 max-w-2xl mx-auto">
            Describe your situation in plain words — typed or spoken, in any Indian language —
            and Urava turns it into a researched memorandum and court-ready drafts.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-4 mb-14">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className="flex items-start gap-4 sm:gap-5 bg-legal-800/40 border border-legal-700 rounded-2xl p-5 sm:p-6"
            >
              <div className="flex-shrink-0 flex items-center gap-3">
                <span className="text-2xl font-bold text-legal-700 w-6 text-right">{i + 1}</span>
                <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center">
                  <step.icon className="h-5 w-5 text-accent" />
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-semibold text-lg mb-1.5">{step.title}</h3>
                <p className="text-legal-300 text-sm leading-relaxed break-words">{step.body}</p>
                {i === 2 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                    {STUDIO.map((s) => (
                      <div key={s.title} className="bg-legal-900/60 border border-legal-700 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <s.icon className="h-4 w-4 text-accent" />
                          <span className="text-white text-sm font-medium">{s.title}</span>
                        </div>
                        <p className="text-legal-400 text-xs leading-relaxed">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Document callout */}
        <div className="bg-legal-800/40 border border-legal-700 rounded-2xl p-6 mb-6 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
            <Upload className="h-5 w-5 text-accent" />
          </div>
          <div>
            <h3 className="text-white font-semibold mb-1.5">Have a document? Attach it.</h3>
            <p className="text-legal-300 text-sm leading-relaxed">
              Upload a charge sheet, FIR, or legal notice — even scanned or handwritten, even in
              Malayalam, Hindi, Tamil or other Indian languages. Urava reads it, translates it,
              and researches or drafts against its actual contents.
            </p>
          </div>
        </div>

        {/* Voice + tours + WhatsApp + free tool, compact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-14">
          {[
            {
              icon: Mic,
              title: 'Speak instead of typing',
              desc: 'Every chat has a mic — dictate in English, Hindi or Malayalam and switch language with one tap.',
            },
            {
              icon: Compass,
              title: 'Learn it inside the app',
              desc: 'Tap "Show me around" — in the user menu, the Today checklist, or the Settings Help card — for short guided tours. Skippable any time, replayable whenever.',
            },
            {
              icon: null,
              title: 'Works on WhatsApp',
              desc: `In court with no laptop? Send your question — or a PDF — to ${WHATSAPP_DISPLAY} and the memo comes back in the chat.`,
              href: WHATSAPP_URL,
            },
            {
              icon: Calculator,
              title: 'Free legal tools',
              desc: 'Citation-backed calculators — limitation periods, default bail, motor-accident compensation, Kerala stamp duty and court fees. No signup.',
              href: '/tools',
            },
          ].map((c) => {
            const inner = (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {c.icon ? (
                    <c.icon className="h-4 w-4 text-accent" />
                  ) : (
                    <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
                  )}
                  <span className="text-white text-sm font-medium">{c.title}</span>
                </div>
                <p className="text-legal-400 text-xs leading-relaxed">{c.desc}</p>
              </>
            );
            const cls = 'bg-legal-800/40 border border-legal-700 rounded-xl p-4 block hover:border-accent/40 transition-colors';
            return c.href ? (
              // Plain <a>: WhatsApp is external; the calculator is an edge-worker page.
              <a key={c.title} href={c.href} className={cls} {...(c.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {inner}
              </a>
            ) : (
              <div key={c.title} className={cls}>{inner}</div>
            );
          })}
        </div>

        {/* Need inspiration */}
        <div className="mb-14">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Need inspiration?</h2>
          <p className="text-legal-400 text-center mb-8 text-sm">
            From everyday problems to courtroom strategy — just type something like these and let
            the assistant take it from there.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {POPULAR.map((q) => (
              <Link
                key={q}
                to="/register"
                className="flex items-start gap-2.5 bg-legal-800/40 border border-legal-700 rounded-xl p-4 text-legal-300 text-sm hover:border-accent/50 hover:text-white transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                <span className="break-words">{q}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-legal-800/50 border border-legal-700 rounded-2xl p-10">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to start?</h2>
          <p className="text-legal-400 mb-6">
            Sign up free — 3 researches and 3 standard drafts every month, no credit card.
          </p>
          <Link
            to="/register"
            className="inline-block bg-accent text-legal-900 font-semibold px-8 py-3 rounded-lg hover:bg-accent-400 transition-colors"
          >
            Start Free →
          </Link>
        </div>
      </div>
    </div>
  );
}
